/** ANC Fitness to Drive Pre-Start Declaration (CONF-20260724). */

export const FITNESS_FORM_VER = 'fitness-anc-v1';

/** Combined declaration version after fitness is required with CCI. */
export const CCI_FITNESS_DECLARATION_TEXT_VER = 'cci+fitness-anc-v1';

export const FITNESS_QUESTION_IDS = [
  'fatigue_free',
  'medication_clear',
  'alcohol_drugs_clear',
  'rest_breaks_ok',
  'seven_hours_rest',
  'driving_hours_ok',
  'other_factors_clear',
] as const;

export type FitnessQuestionId = (typeof FITNESS_QUESTION_IDS)[number];

export interface FitnessAnswers {
  answers: Partial<Record<FitnessQuestionId, boolean | null>>;
  signed_name?: string;
  signed_at?: string;
  form_ver: string;
}

export function emptyFitnessAnswers(): FitnessAnswers {
  const answers: FitnessAnswers['answers'] = {};
  for (const id of FITNESS_QUESTION_IDS) {
    answers[id] = null;
  }
  return { answers, form_ver: FITNESS_FORM_VER };
}

export function parseFitnessAnswers(raw: unknown): FitnessAnswers | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const answersRaw = obj.answers;
  if (!answersRaw || typeof answersRaw !== 'object') return null;
  const answers: FitnessAnswers['answers'] = {};
  for (const id of FITNESS_QUESTION_IDS) {
    const v = (answersRaw as Record<string, unknown>)[id];
    answers[id] = typeof v === 'boolean' ? v : null;
  }
  return {
    answers,
    signed_name: typeof obj.signed_name === 'string' ? obj.signed_name : undefined,
    signed_at: typeof obj.signed_at === 'string' ? obj.signed_at : undefined,
    form_ver: typeof obj.form_ver === 'string' ? obj.form_ver : FITNESS_FORM_VER,
  };
}

export interface EvaluateFitnessResult {
  complete: boolean;
  allYes: boolean;
  failingIds: FitnessQuestionId[];
  incompleteIds: FitnessQuestionId[];
  signed: boolean;
  canDeclare: boolean;
}

/** All seven questions answered Yes and signed → fitness declaration OK. */
export function evaluateFitness(
  fitness: FitnessAnswers | null | undefined,
  opts?: { requireSigned?: boolean }
): EvaluateFitnessResult {
  const requireSigned = opts?.requireSigned !== false;
  const failingIds: FitnessQuestionId[] = [];
  const incompleteIds: FitnessQuestionId[] = [];
  const answers = fitness?.answers ?? {};

  for (const id of FITNESS_QUESTION_IDS) {
    const v = answers[id];
    if (v === null || v === undefined) {
      incompleteIds.push(id);
    } else if (v === false) {
      failingIds.push(id);
    }
  }

  const complete = incompleteIds.length === 0;
  const allYes = complete && failingIds.length === 0;
  const signed = !!(fitness?.signed_name?.trim() && fitness?.signed_at);
  const canDeclare = allYes && (!requireSigned || signed);

  return { complete, allYes, failingIds, incompleteIds, signed, canDeclare };
}

export function isFitnessPassing(raw: unknown): boolean {
  const parsed = parseFitnessAnswers(raw);
  return evaluateFitness(parsed, { requireSigned: true }).canDeclare;
}
